/**
 * A saree costing as the server keeps it (KarkhanaCosting), and the same sums worked out in the
 * browser so totals move as people type. The server works everything out again on save; its numbers
 * are the ones that count.
 */
export type Kind = "YARN" | "LABOUR" | "QTY_RATE" | "SHARED" | "LUMP" | "PERCENT_OF_MATERIAL";
export type Method = "ACTUAL" | "PLANNED" | "DAYS" | "MANUAL";
export type Group = "MATERIAL" | "WEAVING" | "PREPARATION" | "SETUP" | "FINISHING" | "OTHER";

export type Line = {
  id?: string;
  key?: string;
  group: Group;
  label: string;
  labelHi?: string | null;
  kind: Kind;
  khariGrams?: string | null;
  koriFactor?: string | null;
  ratePerKg?: string | null;
  materialRateId?: string | null;
  materialRateName?: string | null;
  qty?: string | null;
  unit?: string | null;
  rate?: string | null;
  sharedTotal?: string | null;
  sharedSarees?: string | null;
  percent?: string | null;
  method?: Method | null;
  actualAmount?: string | null;
  plannedInches?: string | null;
  ratePerInch?: string | null;
  days?: string | null;
  dayRate?: string | null;
  workTypeCodes?: string[];
  activityCodes?: string[];
  amount?: string | null;
  source?: string | null;
  sourceNote?: string | null;
  note?: string | null;
};

export type Layers = {
  overheadPct?: string | null;
  profitPct?: string | null;
  discountPct?: string | null;
  gstPct?: string | null;
  roundTo?: string | null;
};

export type Header = {
  pieceId?: string | null;
  sareeNumber?: string | null;
  designId?: string | null;
  designNumber?: string | null;
  designName?: string | null;
  loomId?: string | null;
  loomCode?: string | null;
  weavers?: string | null;
  productUniqueId?: string | null;
  productName?: string | null;
  keepOwnPrice?: string[];
  weightGrams?: string | null;
  lengthInches?: string | null;
  loomType?: string | null;
  reed?: string | null;
  warpDenier?: string | null;
  weftDenier?: string | null;
  epi?: string | null;
  ppi?: string | null;
};

export type Totals = {
  material: number;
  weaving: number;
  preparation: number;
  setup: number;
  finishing: number;
  other: number;
  production: number;
  overhead: number;
  costPrice: number;
  profit: number;
  salePrice: number;
  discount: number;
  saleAfterDiscount: number;
  gst: number;
  finalPrice: number;
  marginPct: number | null;
};

export type Blank = { lineId: string; label: string; field: string };

export const GROUPS: { id: Group; title: string; hi: string }[] = [
  { id: "MATERIAL", title: "Material (yarn and zari)", hi: "सामान (धागा और ज़री)" },
  { id: "WEAVING", title: "Weaving labour", hi: "बुनाई मज़दूरी" },
  { id: "PREPARATION", title: "Preparation", hi: "तैयारी" },
  { id: "SETUP", title: "Setup (shared by many sarees)", hi: "सेटअप (कई साड़ियों में बँटा)" },
  { id: "FINISHING", title: "Finishing and dispatch", hi: "फ़िनिशिंग और भेजना" },
  { id: "OTHER", title: "Other", hi: "अन्य" },
];

export const KINDS: { id: Kind; title: string }[] = [
  { id: "LUMP", title: "Amount (₹)" },
  { id: "QTY_RATE", title: "Quantity × rate" },
  { id: "YARN", title: "Yarn: grams × ₹/kg" },
  { id: "SHARED", title: "Shared: total ÷ sarees" },
  { id: "LABOUR", title: "Labour (paid / planned / days)" },
  { id: "PERCENT_OF_MATERIAL", title: "% of material" },
];

export const METHODS: { id: Method; title: string }[] = [
  { id: "ACTUAL", title: "Paid in Karkhana" },
  { id: "PLANNED", title: "Planned (inches × ₹/in)" },
  { id: "DAYS", title: "Days × ₹/day" },
  { id: "MANUAL", title: "Type amount" },
];

/** A typed number, or null while empty. "1,250", "₹ 1250" and "15%" are fine; anything else is NaN. */
export const num = (raw?: string | null): number | null => {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).replace(/,/g, "").replace(/₹/g, "").replace(/%/g, "").trim();
  if (!s) return null;
  if (!/^\d*\.?\d+$|^\d+\.$/.test(s)) return NaN;
  return Number(s);
};

const money = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export const roundUp = (v: number, step: number | null) => {
  if (!step) return money(v);
  // Small tolerance so 26000.0000001 doesn't jump to the next step.
  return Math.ceil(v / step - 1e-9) * step;
};

type Need = (raw: string | null | undefined, field: string) => number | null;

/** What a line comes to, or null while something is empty. */
export const lineAmount = (l: Line, need: Need): number | null => {
  switch (l.kind) {
    case "YARN": {
      const k = need(l.khariGrams, "khariGrams");
      const f = need(l.koriFactor, "koriFactor");
      const r = need(l.ratePerKg, "ratePerKg");
      return k === null || f === null || r === null ? null : money((k * f * r) / 1000);
    }
    case "QTY_RATE": {
      const q = need(l.qty, "qty");
      const r = need(l.rate, "rate");
      return q === null || r === null ? null : money(q * r);
    }
    case "SHARED": {
      const t = need(l.sharedTotal, "sharedTotal");
      const n = need(l.sharedSarees, "sharedSarees");
      return t === null || n === null || n === 0 ? null : money(t / n);
    }
    case "LABOUR": {
      if (!l.method) {
        need(null, "method");
        return null;
      }
      if (l.method === "ACTUAL") return need(l.actualAmount, "actualAmount");
      if (l.method === "PLANNED") {
        const i = need(l.plannedInches, "plannedInches");
        const r = need(l.ratePerInch, "ratePerInch");
        return i === null || r === null ? null : money(i * r);
      }
      if (l.method === "DAYS") {
        const d = need(l.days, "days");
        const r = need(l.dayRate, "dayRate");
        return d === null || r === null ? null : money(d * r);
      }
      return need(l.amount, "amount");
    }
    case "PERCENT_OF_MATERIAL":
      return null; // worked out with the material total
    default:
      return need(l.amount, "amount");
  }
};

export const planned = (l: Line): number | null => {
  const i = num(l.plannedInches);
  const r = num(l.ratePerInch);
  return i === null || r === null || isNaN(i) || isNaN(r) ? null : money(i * r);
};

export const byDays = (l: Line): number | null => {
  const d = num(l.days);
  const r = num(l.dayRate);
  return d === null || r === null || isNaN(d) || isNaN(r) ? null : money(d * r);
};

export type Computed = { amounts: Record<string, number | null>; totals: Totals; blanks: Blank[]; bad: Blank[] };

/** The sheet's sums, same rules as the server (CostingMath). */
export const compute = (lines: Line[], layers: Layers, header: Header): Computed => {
  const blanks: Blank[] = [];
  const bad: Blank[] = [];
  const amounts: Record<string, number | null> = {};
  const byGroup: Record<Group, number> = { MATERIAL: 0, WEAVING: 0, PREPARATION: 0, SETUP: 0, FINISHING: 0, OTHER: 0 };
  let materialBase = 0;
  const needFor = (l: Line, id: string): Need => (raw, field) => {
    const n = num(raw);
    if (n === null) {
      blanks.push({ lineId: id, label: l.label, field });
      return null;
    }
    if (isNaN(n)) {
      bad.push({ lineId: id, label: l.label, field });
      return null;
    }
    return n;
  };
  lines.forEach((l, i) => {
    const id = l.id || `new-${i}`;
    if (l.kind === "PERCENT_OF_MATERIAL") return;
    const a = lineAmount(l, needFor(l, id));
    amounts[id] = a;
    if (a !== null) {
      byGroup[l.group] = (byGroup[l.group] || 0) + a;
      if (l.group === "MATERIAL") materialBase += a;
    }
  });
  lines.forEach((l, i) => {
    const id = l.id || `new-${i}`;
    if (l.kind !== "PERCENT_OF_MATERIAL") return;
    const p = needFor(l, id)(l.percent, "percent");
    const a = p === null ? null : money((materialBase * p) / 100);
    amounts[id] = a;
    if (a !== null) byGroup[l.group] = (byGroup[l.group] || 0) + a;
  });
  const layer = (raw: string | null | undefined, field: string, label: string) => {
    const n = num(raw);
    if (n === null) blanks.push({ lineId: "layers", label, field });
    else if (isNaN(n)) bad.push({ lineId: "layers", label, field });
    return n === null || isNaN(n) ? null : n;
  };
  const oh = layer(layers.overheadPct, "overheadPct", "Business overhead %");
  const pr = layer(layers.profitPct, "profitPct", "Profit %");
  const di = layer(layers.discountPct, "discountPct", "Discount %");
  const gs = layer(layers.gstPct, "gstPct", "GST %");
  const ro = layer(layers.roundTo, "roundTo", "Round sale price to");
  if (num(header.weightGrams) === null) blanks.push({ lineId: "header", label: "Saree weight (grams)", field: "weightGrams" });

  const production = money(Object.values(byGroup).reduce((s, v) => s + v, 0));
  const overhead = money((production * (oh || 0)) / 100);
  const costPrice = money(production + overhead);
  const salePrice = roundUp(costPrice + (costPrice * (pr || 0)) / 100, ro);
  const discount = money((salePrice * (di || 0)) / 100);
  const saleAfterDiscount = money(salePrice - discount);
  const gst = money((saleAfterDiscount * (gs || 0)) / 100);
  return {
    amounts,
    blanks,
    bad,
    totals: {
      material: money(byGroup.MATERIAL),
      weaving: money(byGroup.WEAVING),
      preparation: money(byGroup.PREPARATION),
      setup: money(byGroup.SETUP),
      finishing: money(byGroup.FINISHING),
      other: money(byGroup.OTHER),
      production,
      overhead,
      costPrice,
      profit: money(salePrice - costPrice),
      salePrice,
      discount,
      saleAfterDiscount,
      gst,
      finalPrice: money(saleAfterDiscount + gst),
      marginPct: saleAfterDiscount ? Math.round(((saleAfterDiscount - costPrice) * 1000) / saleAfterDiscount) / 10 : null,
    },
  };
};

/** Kori grams for a yarn line, for showing beside the khari input. */
export const koriGrams = (l: Line): number | null => {
  const k = num(l.khariGrams);
  const f = num(l.koriFactor);
  return k === null || f === null || isNaN(k) || isNaN(f) ? null : Math.round(k * f * 100) / 100;
};

export const newLine = (group: Group, kind: Kind): Line => ({
  group,
  kind,
  label: "",
  koriFactor: kind === "YARN" ? "1" : null,
  method: kind === "LABOUR" ? "MANUAL" : null,
});

export const STATUS_LABEL: Record<string, string> = {
  DRAFT: "Draft",
  SUBMITTED: "Waiting for approval",
  APPROVED: "Approved",
  REJECTED: "Sent back",
  SUPERSEDED: "Replaced",
};

/** Plain words for audit field names. */
export const FIELD_LABEL: Record<string, string> = {
  khariGrams: "khari grams",
  koriFactor: "kori factor",
  ratePerKg: "₹/kg",
  qty: "quantity",
  rate: "rate",
  sharedTotal: "shared total",
  sharedSarees: "sarees sharing",
  percent: "%",
  method: "method",
  actualAmount: "paid in Karkhana",
  plannedInches: "planned inches",
  ratePerInch: "₹/inch",
  days: "days",
  dayRate: "₹/day",
  amount: "amount",
  label: "name",
  labelHi: "Hindi name",
  overheadPct: "overhead %",
  profitPct: "profit %",
  discountPct: "discount %",
  gstPct: "GST %",
  roundTo: "round to",
  weightGrams: "saree weight",
  lengthInches: "length",
  costPrice: "product cost price",
  sellPrice: "product sale price",
};

/** The calendar day of a saved time where the studio is (not the UTC day), as yyyy-mm-dd for fmtDay. */
export const localDay = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso).slice(0, 10);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
